import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { 
  AdventureHeader,
  AdventureScene,
  AdventureItem,
  AdventureStat,
  Player, 
  GameSession, 
  GameStatus,
  ApiError,
  UserInputActionResponse,
  PlaythroughSnapshotListResult,
  PlaythroughSnapshotEngineRestoreResult,
} from '../types';
import {
  CustomStyle,
  SceneSection,
  RichTextDescription,
  RichTextElement,
  ActivityDefinition,
  ActivityTypeDefinition
} from 'coop-adventure-engine';
import {
  CHARACTER_ACTIVITIES_IN_PROGRESS_VAR,
  CHARACTER_ACTIVITIES_COMPLETED_VAR,
  CHARACTER_ACTIVITY_RESUME_SCENES_VAR
} from '../constants/characterActivityVarNames';
import apiService from '../services/api';
import useUIStore from './uiStore';
import { getPlayerCharacterName, showAdventureToastsForPlayer } from '../utils/adventureToast';
import useAuthStore from './authStore';
import guestService from '../services/guestService';
import authService from '../services/auth';

type UserVariableScope = 'scene' | 'character' | 'player' | 'adventure' | 'user';

interface UserVariableValueState {
  value: string;
  scope?: UserVariableScope;
}

type UserVariableMap = Record<string, UserVariableValueState>;

const createVariableValueState = (
  value: string,
  scope?: UserVariableScope,
  fallbackScope?: UserVariableScope
): UserVariableValueState => {
  const entry: UserVariableValueState = { value };
  const resolvedScope = scope ?? fallbackScope;
  if (resolvedScope !== undefined) {
    entry.scope = resolvedScope;
  }
  return entry;
};

interface WaitingCondition {
  playerId: string;
  optionId: string;
  message: string;
  waitingFor?: string[];
}

const isRichTextElement = (value: unknown): value is RichTextElement =>
  typeof value === 'object' && value !== null && 'type' in (value as Record<string, unknown>);

// Coalesce concurrent loadCurrentScene calls for the same session+player (single in-flight request)
let loadCurrentScenePromise: Promise<void> | null = null;
let loadCurrentSceneKey: string | null = null;

/**
 * Bumped when a newer scene/session is applied (selectOption, loadSessionState, loadSystemSession).
 * Used to discard stale in-flight `loadCurrentScene({ backgroundRefresh: true })` GETs that
 * started before that apply (avoids overwriting a fresh result — including after guest→auth
 * session swap where a guest refresh must not overwrite the authenticated player's id).
 */
let sceneApplyEpoch = 0;

/** Options for {@link GameStore.loadCurrentScene} */
export type LoadCurrentSceneOptions = {
  /**
   * When true (WebSocket-driven refreshes): do not force `isLoading` false, and allow
   * superseding by a newer scene/session apply (stale GET discarded).
   */
  backgroundRefresh?: boolean;
};

// Guard so only one auto-transition runs at a time (e.g. after poll or waiting-update)
let autoTransitionInProgress = false;

const applyUserVariableToElement = (
  element: RichTextElement,
  variableMap: UserVariableMap
): { updated: RichTextElement; mutated: boolean } => {
  let mutated = false;
  let updatedElement = element;

  if (element.type === 'user-input') {
    const variableName = element.userInputAttributes?.variable;
    if (variableName) {
      const targetValue = variableMap[variableName]?.value ?? '';
      const currentValue =
        typeof element.content === 'string'
          ? element.content
          : Array.isArray(element.content)
            ? ''
            : '';

      if (currentValue !== targetValue) {
        updatedElement = {
          ...element,
          content: targetValue
        };
        mutated = true;
      }
    }
  }

  if (element.type === 'spinner') {
    const variableName = element.spinnerAttributes?.variable;
    if (variableName) {
      const targetValue = variableMap[variableName]?.value ?? '';
      const currentValue = typeof element.content === 'string' ? element.content : '';

      if (currentValue !== targetValue) {
        updatedElement = {
          ...element,
          content: targetValue
        };
        mutated = true;
      }
    }
  }

  if (element.type === 'color-picker') {
    const variableName = element.colorPickerAttributes?.variable;
    if (variableName) {
      const targetValue = variableMap[variableName]?.value ?? '';
      const currentValue = typeof element.content === 'string' ? element.content : '';

      if (currentValue !== targetValue) {
        updatedElement = {
          ...element,
          content: targetValue
        };
        mutated = true;
      }
    }
  }

  if (element.type === 'color-sample') {
    const variableName = element.colorSampleAttributes?.variable;
    if (variableName) {
      const targetValue = variableMap[variableName]?.value ?? '';
      const currentValue = typeof element.content === 'string' ? element.content : '';

      if (currentValue !== targetValue) {
        updatedElement = {
          ...element,
          content: targetValue
        };
        mutated = true;
      }
    }
  }

  if (element.type === 'time-picker') {
    const variableName = element.timePickerAttributes?.variable;
    if (variableName) {
      const targetValue = variableMap[variableName]?.value ?? '';
      const currentValue = typeof element.content === 'string' ? element.content : '';

      if (currentValue !== targetValue) {
        updatedElement = {
          ...element,
          content: targetValue
        };
        mutated = true;
      }
    }
  }

  if (element.type === 'date-picker') {
    const variableName = element.datePickerAttributes?.variable;
    if (variableName) {
      const targetValue = variableMap[variableName]?.value ?? '';
      const currentValue = typeof element.content === 'string' ? element.content : '';

      if (currentValue !== targetValue) {
        updatedElement = {
          ...element,
          content: targetValue
        };
        mutated = true;
      }
    }
  }

  if (Array.isArray(element.content) && element.content.every(isRichTextElement)) {
    const originalChildren = element.content as RichTextElement[];
    let childrenMutated = false;

    const updatedChildren = originalChildren.map((child) => {
      const { updated, mutated: childMutated } = applyUserVariableToElement(child, variableMap);
      if (childMutated) {
        childrenMutated = true;
      }
      return updated;
    });

    if (childrenMutated) {
      updatedElement = mutated
        ? { ...updatedElement, content: updatedChildren }
        : { ...element, content: updatedChildren };
      mutated = true;
    }
  }

  return { updated: updatedElement, mutated };
};

const updateRichTextDescriptionWithVariables = (
  description: string | RichTextDescription,
  variableMap: UserVariableMap
): string | RichTextDescription => {
  if (!description || typeof description === 'string') {
    return description;
  }

  let mutated = false;
  const updatedElements = description.elements.map((element) => {
    const { updated, mutated: elementMutated } = applyUserVariableToElement(element, variableMap);
    if (elementMutated) {
      mutated = true;
    }
    return updated;
  });

  if (!mutated) {
    return description;
  }

  return {
    ...description,
    elements: updatedElements
  };
};

const updateSceneSectionsWithVariables = (
  sections: SceneSection[] | undefined,
  variableMap: UserVariableMap
): SceneSection[] | undefined => {
  if (!sections || sections.length === 0) {
    return sections;
  }

  let mutated = false;
  const updatedSections = sections.map((section) => {
    let sectionMutated = false;
    let updatedDescription: string | RichTextDescription | undefined = section.description;

    if (section.description) {
      const nextDescription = updateRichTextDescriptionWithVariables(section.description, variableMap);
      if (nextDescription !== section.description) {
        sectionMutated = true;
        mutated = true;
        updatedDescription = nextDescription as string | RichTextDescription;
      }
    }

    return sectionMutated
      ? {
          ...section,
          description: (updatedDescription ?? section.description) as string | RichTextDescription
        }
      : section;
  });

  return mutated ? (updatedSections as SceneSection[]) : sections;
};

const updateSceneWithVariables = (
  scene: { id: string; sections: SceneSection[]; style?: CustomStyle } | null,
  variableMap: UserVariableMap
) => {
  if (!scene) {
    return scene;
  }

  const updatedSections = updateSceneSectionsWithVariables(scene.sections, variableMap);
  if (updatedSections === scene.sections) {
    return scene;
  }

  return {
    ...scene,
    sections: updatedSections ?? []
  };
};

const collectUserInputValuesFromElement = (
  element: RichTextElement,
  accumulator: UserVariableMap
) => {
  if (!element) {
    return;
  }

  if (element.type === 'user-input') {
    const variableName = element.userInputAttributes?.variable;
    if (variableName) {
      const contentValue =
        typeof element.content === 'string'
          ? element.content
          : Array.isArray(element.content)
            ? ''
            : '';
      const previousScope = accumulator[variableName]?.scope;
      accumulator[variableName] = createVariableValueState(contentValue, undefined, previousScope);
    }
  }

  if (element.type === 'spinner') {
    const variableName = element.spinnerAttributes?.variable;
    if (variableName) {
      const contentValue = typeof element.content === 'string' ? element.content : '';
      const previousScope = accumulator[variableName]?.scope;
      const existingValue = accumulator[variableName]?.value ?? '';
      let effectiveValue = contentValue;
      // When backend has not persisted a choice yet, mirror the spinner UI default
      // (explicit default attribute, else first values entry).
      if (effectiveValue === '' && existingValue === '') {
        const valuesCsv = element.spinnerAttributes?.values ?? '';
        const options = valuesCsv
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean);
        const defaultAttr = element.spinnerAttributes?.default?.trim();
        if (defaultAttr && options.includes(defaultAttr)) {
          effectiveValue = defaultAttr;
        } else if (options.length > 0) {
          effectiveValue = options[0] ?? '';
        }
      }
      if (effectiveValue !== '') {
        accumulator[variableName] = createVariableValueState(
          effectiveValue,
          undefined,
          previousScope
        );
      }
    }
  }

  if (element.type === 'color-picker') {
    const variableName = element.colorPickerAttributes?.variable;
    if (variableName) {
      const contentValue = typeof element.content === 'string' ? element.content : '';
      const previousScope = accumulator[variableName]?.scope;
      if (contentValue !== '') {
        accumulator[variableName] = createVariableValueState(contentValue, undefined, previousScope);
      }
    }
  }

  if (element.type === 'color-sample') {
    const variableName = element.colorSampleAttributes?.variable;
    if (variableName) {
      const contentValue = typeof element.content === 'string' ? element.content : '';
      const previousScope = accumulator[variableName]?.scope;
      // Always trust backend-interpolated color-sample content (including empty) so a cleared
      // adventure variable is not masked by stale userVariableValues from an earlier scene.
      accumulator[variableName] = createVariableValueState(
        contentValue,
        undefined,
        previousScope
      );
    }
  }

  if (element.type === 'time-picker') {
    const variableName = element.timePickerAttributes?.variable;
    if (variableName) {
      const contentValue = typeof element.content === 'string' ? element.content : '';
      if (contentValue !== '') {
        const previousScope = accumulator[variableName]?.scope;
        accumulator[variableName] = createVariableValueState(contentValue, undefined, previousScope);
      }
    }
  }

  if (element.type === 'date-picker') {
    const variableName = element.datePickerAttributes?.variable;
    if (variableName) {
      const contentValue = typeof element.content === 'string' ? element.content : '';
      if (contentValue !== '') {
        const previousScope = accumulator[variableName]?.scope;
        accumulator[variableName] = createVariableValueState(contentValue, undefined, previousScope);
      }
    }
  }

  if (Array.isArray(element.content)) {
    for (const child of element.content) {
      if (isRichTextElement(child)) {
        collectUserInputValuesFromElement(child, accumulator);
      }
    }
  }
};

const extractUserInputValuesFromDescription = (
  description: string | RichTextDescription | undefined
): UserVariableMap => {
  if (!description || typeof description === 'string') {
    return {};
  }

  const accumulator: UserVariableMap = {};
  description.elements.forEach((element) => collectUserInputValuesFromElement(element, accumulator));
  return accumulator;
};

const extractUserInputValuesFromSections = (sections: SceneSection[] | undefined): UserVariableMap => {
  if (!sections || sections.length === 0) {
    return {};
  }

  return sections.reduce<UserVariableMap>((accumulator, section) => {
    const extracted = extractUserInputValuesFromDescription(section.description);
    return mergeUserVariableMaps(accumulator, extracted);
  }, {});
};

const mergeUserVariableMaps = (
  base: UserVariableMap,
  updates: UserVariableMap,
  options?: { preserveExistingWhenNewEmpty?: boolean; pendingVariableNames?: string[] }
): UserVariableMap => {
  if (!updates || Object.keys(updates).length === 0) {
    return base;
  }

  const merged: UserVariableMap = { ...base };
  const preserve = options?.preserveExistingWhenNewEmpty === true;
  const pendingNames = options?.pendingVariableNames;

  Object.entries(updates).forEach(([variableName, valueState]) => {
    const previous = merged[variableName];
    const newValue = valueState.value ?? '';
    const existingValue = previous?.value ?? '';
    // Only preserve when there is uncommitted pending input for this variable; otherwise trust backend.
    const hasPendingInput = !pendingNames || pendingNames.includes(variableName);
    const didPreserve = preserve && existingValue !== '' && hasPendingInput;
    const finalValue = didPreserve ? existingValue : newValue;

    const entry = createVariableValueState(
      finalValue,
      valueState.scope,
      previous?.scope
    );
    merged[variableName] = entry;
  });
  return merged;
};

const mergeActivityVariablesFromResponse = (
  base: UserVariableMap,
  activityVariables?: {
    character_activities_in_progress: string;
    character_activities_completed: string;
    character_activity_resume_scenes?: string;
  } | null
): UserVariableMap => {
  if (!activityVariables) {
    return base;
  }
  const merged: UserVariableMap = mergeUserVariableMaps(base, {
    [CHARACTER_ACTIVITIES_IN_PROGRESS_VAR]: createVariableValueState(
      activityVariables.character_activities_in_progress,
      'character'
    ),
    [CHARACTER_ACTIVITIES_COMPLETED_VAR]: createVariableValueState(
      activityVariables.character_activities_completed,
      'character'
    )
  });
  if (activityVariables.character_activity_resume_scenes !== undefined) {
    merged[CHARACTER_ACTIVITY_RESUME_SCENES_VAR] = createVariableValueState(
      activityVariables.character_activity_resume_scenes,
      'character'
    );
  }
  return merged;
};

const buildOptionsMapFromSections = (sections: SceneSection[] = []) => {
  const optionsMap: Record<string, any> = {};
  sections.forEach((section) => {
    const options = (section as any).options ?? [];
    options.forEach((option: any) => {
      if (option?.id) {
        optionsMap[option.id] = option;
      }
    });
  });
  return optionsMap;
};

/** For same-scene reload, include pending input drafts so they are preserved when backend returns empty. */
const applyPendingInputsToVariableMap = (
  base: UserVariableMap,
  pending: Record<string, { value: string; maxLength?: number }>
): UserVariableMap => {
  if (!pending || Object.keys(pending).length === 0) return base;
  const next = { ...base };
  Object.entries(pending).forEach(([name, entry]) => {
    const value = entry?.value;
    if (value == null) return;
    const previous = next[name];
    next[name] = createVariableValueState(
      value,
      previous?.scope,
      previous?.scope
    );
  });
  return next;
};

const prepareSceneState = (
  scene: { id: string; sections: SceneSection[]; style?: CustomStyle } | null,
  availableOptionIds: string[] = [],
  waitingOptionIds: string[] = [],
  existingVariables: UserVariableMap = {},
  options?: { preserveExistingWhenNewEmpty?: boolean; pendingVariableNames?: string[]; backOptionId?: string | null }
) => {
  if (!scene) {
    return {
      scene: null,
      availableOptionIds: [],
      waitingOptionIds: [],
      backOptionId: null as string | null,
      optionsMap: {},
      userVariableValues: existingVariables
    };
  }

  const extractedVariables = extractUserInputValuesFromSections(scene.sections);
  const mergedVariables = mergeUserVariableMaps(
    existingVariables,
    extractedVariables,
    options
  );
  const normalizedScene = updateSceneWithVariables(scene, mergedVariables);
  const optionsMap = buildOptionsMapFromSections(normalizedScene?.sections ?? []);

  return {
    scene: normalizedScene,
    availableOptionIds,
    waitingOptionIds,
    backOptionId: options?.backOptionId ?? null,
    optionsMap,
    userVariableValues: mergedVariables
  };
};

interface GameState {
  // Current session
  currentSession: GameSession | null;
  currentPlayer: any | null;
  
  // Adventure data (header includes style and character info)
  currentAdventure: AdventureHeader | null;
  /** Activity definitions for the current session adventure (for `<Activity-List>` UI). */
  adventureActivities: ActivityDefinition[];
  adventureActivityTypes: ActivityTypeDefinition[];
  
  // Adventure selection state
  availableAdventures: AdventureHeader[];
  adventureIds: string[];
  selectedAdventureId: string | null;
  selectedAdventure: AdventureHeader | null;
  isLoadingAdventures: boolean;
  adventureError: string | null;
  
  // Session lobby state
  sessionPlayers: Player[];
  sessionStatus: 'waiting' | 'active' | 'completed' | 'paused';
  isCreatingSession: boolean;
  sessionError: string | null;
  currentScene: {
    id: string;
    sections: SceneSection[];
    style?: CustomStyle;
  } | null;
  availableOptionIds: string[];
  waitingOptionIds: string[];
  backOptionId: string | null;
  optionsMap: Record<string, {
    id: string;
    text: string;
    target?: string;
    condition?: string;
    effect?: string;
    waitMessage?: string;
    autoTransition?: boolean;
  }>;
  
  // Player state
  players: any[];
  playerStates: Record<string, any>;
  
  // Location inventory state
  locationInventories: Record<string, string[]>; // locationTag -> itemTags[]
  
  // Real-time updates
  isConnected: boolean;
  /** True after the socket receives join-confirmed for the current session (safe to send player-action). */
  isSocketSessionJoined: boolean;
  /** Socket.IO transport name when connected (e.g. polling, websocket). */
  socketTransport: string | null;
  waitingConditions: WaitingCondition[];
  reconnectAttempts: number;
  userVariableValues: UserVariableMap;
  pendingUserInputs: Record<string, { value: string; maxLength?: number }>;
  
  // Loading and error states
  isLoading: boolean;
  error: ApiError | null;

  // Config from backend (set when health/config is loaded)
  systemAdventureId: string;

  /** True while START_ADVENTURE is in progress so URL-sync effect navigates instead of clearing (survives Strict Mode remount) */
  startAdventureTransitionInProgress: boolean;

  // Actions
  setSystemAdventureId: (id: string) => void;
  setStartAdventureTransitionInProgress: (value: boolean) => void;
  joinSession: (sessionId: string) => Promise<void>;
  leaveSession: () => void;
  selectOption: (optionId: string, options?: { deliverEffectToasts?: boolean }) => Promise<boolean>; // Returns true if player completed
  selectFinishOption: (optionId?: string) => Promise<void>;
  selectLeaveOption: (optionId?: string) => Promise<void>;
  updatePlayerState: (updates: any) => void;
  setCurrentScene: (scene: { 
    id: string; 
    sections: SceneSection[];
    style?: CustomStyle;
  }) => void;
  updateGameState: (state: any) => void;
  setConnectionStatus: (isConnected: boolean) => void;
  setSocketSessionJoined: (joined: boolean) => void;
  setSocketTransport: (transport: string | null) => void;
  addWaitingCondition: (condition: WaitingCondition) => void;
  removeWaitingCondition: (playerId: string, optionId: string) => void;
  setReconnectAttempts: (attempts: number) => void;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
  setUserVariableValue: (variableName: string, value: string, scope?: UserVariableScope) => void;
  setUserVariableValues: (values: UserVariableMap) => void;
  setPendingUserInput: (
    variableName: string,
    value: string,
    options?: { maxLength?: number }
  ) => void;
  clearPendingUserInput: (variableName: string) => void;
  flushPendingUserInputs: () => Promise<void>;
  
  // Adventure selection actions
  loadAdventures: () => Promise<void>;
  selectAdventure: (adventureId: string) => void;
  clearAdventureError: () => void;
  
  // Adventure management
  loadAdventure: (adventureId: string) => Promise<void>;
  loadAdventureHeader: (adventureId: string) => Promise<void>;
  loadAdventureScene: (adventureId: string, sceneId: string) => Promise<AdventureScene>;
  loadAdventureItem: (adventureId: string, itemName: string) => Promise<AdventureItem>;
  loadAdventureStat: (adventureId: string, statName: string) => Promise<AdventureStat>;
  createSession: (adventureId: string) => Promise<{ sessionId: string; playerId?: string }>;
  createGuestSession: (
    adventureId: string,
    invitePayload?: { variables?: Record<string, string> }
  ) => Promise<{ sessionId: string; playerId?: string }>;
  redeemGuestAdventureInvite: (token: string) => Promise<{ sessionId: string; playerId: string }>;
  startSession: () => Promise<void>;
  loadCurrentScene: (options?: LoadCurrentSceneOptions) => Promise<void>;
  /** After a scene load, if any available option has autoTransition, select it (e.g. max-time timer expiry). */
  checkAndFireAutoTransition: () => Promise<void>;
  
  // Load session state when player already exists (e.g., after joining via invite)
  loadSessionState: (sessionId: string, playerId: string, playerData?: any) => Promise<void>;
  
  // Session lobby actions
  joinSessionByCode: (sessionCode: string) => Promise<void>;
  clearSessionError: () => void;
  loadSessionPlayers: (sessionId: string) => Promise<void>;
  listPlaythroughSnapshots: () => Promise<PlaythroughSnapshotListResult>;
  loadPlaythroughSnapshot: (snapshotPath: string) => Promise<PlaythroughSnapshotEngineRestoreResult>;
  
  // Session management actions
  deleteSession: (sessionId: string) => Promise<void>;
  leaveSessionById: (sessionId: string) => Promise<void>;
  submitUserInput: (
    variableName: string,
    value: string,
    options?: { maxLength?: number }
  ) => Promise<UserInputActionResponse>;
  
  // Location inventory actions
  updateLocationInventory: (locationTag: string, items: string[]) => void;
  addItemToLocation: (locationTag: string, itemTag: string) => void;
  removeItemFromLocation: (locationTag: string, itemTag: string) => void;
  getLocationInventory: (locationTag: string) => string[];
  
  // Helper actions
  loadSystemSession: (inviteVariables?: Record<string, string>) => Promise<{ sessionId: string; playerId: string }>;
  clearSessionState: () => void;
}

const useGameStore = create<GameState>()(
  persist(
    (set, get) => ({
      // Initial state
      currentSession: null,
      currentPlayer: null,
      currentAdventure: null,
      adventureActivities: [],
      adventureActivityTypes: [],
      
      // Adventure selection state
      availableAdventures: [],
      adventureIds: [],
      selectedAdventureId: null,
      selectedAdventure: null,
      isLoadingAdventures: false,
      adventureError: null,
      
      // Session lobby state
      sessionPlayers: [],
      sessionStatus: 'waiting',
      isCreatingSession: false,
      sessionError: null,
      
      currentScene: null,
      availableOptionIds: [],
      waitingOptionIds: [],
      backOptionId: null,
      optionsMap: {},
      players: [],
      playerStates: {},
      locationInventories: {},
      isConnected: false,
      isSocketSessionJoined: false,
      socketTransport: null,
      waitingConditions: [],
      reconnectAttempts: 0,
      userVariableValues: {},
      pendingUserInputs: {},
      isLoading: false,
      error: null,
      systemAdventureId: 'storynity',
      startAdventureTransitionInProgress: false,

      setSystemAdventureId: (id: string) => set({ systemAdventureId: id }),
      setStartAdventureTransitionInProgress: (value: boolean) => set({ startAdventureTransitionInProgress: value }),

      // Actions
      joinSession: async (sessionId: string) => {
        set({ isLoading: true, error: null });
        
        try {
          // Clear previous session state before joining new session
          // This ensures clean state when switching between sessions
          get().clearSessionState();
          
          // Get session and players first to determine available playerSlot
          const session = await apiService.getSession(sessionId);
          const players = await apiService.getSessionPlayers(sessionId);
          
          // Get adventure to find available player slots
          let adventure = null;
          if (session.adventureId) {
            try {
              adventure = await apiService.getAdventureHeader(session.adventureId);
            } catch (adventureError) {
              console.warn('Failed to load adventure details:', adventureError);
            }
          }
          
          // Determine available playerSlot
          let playerSlot = '';
          if (adventure && adventure.players) {
            // Find the first player slot that's not already taken
            // Players may have playerName field - check both the field name and ensure we're comparing correctly
            const takenSlots = new Set(
              players
                .map(p => {
                  // Try different possible field names
                  return (p as any).playerName || (p as any).player_name || (p as any).playerName;
                })
                .filter(Boolean)
            );
            
            // Find first available slot from adventure definition
            const availableSlot = adventure.players.find(p => {
              const slotName = p.playerName;
              return slotName && !takenSlots.has(slotName);
            });
            
            if (availableSlot) {
              playerSlot = availableSlot.playerName;
            } else {
              // If no slot found, it means all slots are taken or there's a mismatch
              console.error('[joinSession] No available slot found:', {
                adventurePlayers: adventure.players.map(p => p.playerName),
                takenSlots: Array.from(takenSlots),
                playersData: players.map(p => ({ id: p.id, playerName: (p as any).playerName, allFields: Object.keys(p) }))
              });
              throw new Error('No available player slots in this session');
            }
          } else {
            throw new Error('Unable to determine available player slot - adventure not loaded');
          }
          
          const player = await apiService.joinSession(sessionId, playerSlot);
          
          // Adventure already loaded above for playerSlot determination
          set({
            currentSession: session,
            currentPlayer: player,
            currentAdventure: adventure, // Set currentAdventure for useAdventureStyle
            sessionPlayers: players,
            sessionStatus: session.status as 'waiting' | 'active' | 'completed' | 'paused',
            selectedAdventure: adventure,
            selectedAdventureId: session.adventureId || null,
            isLoading: false,
            error: null,
          });
          
          // Load current scene and options
          await get().loadCurrentScene();
          
        } catch (error) {
          set({
            isLoading: false,
            error: error as ApiError,
          });
          throw error;
        }
      },

      loadSessionState: async (sessionId: string, playerId: string, playerData?: any) => {
        if (import.meta.env.DEV) {
          console.log('[GameStore] loadSessionState: start', { sessionId, playerId });
        }
        // Keep any existing scene on screen while we fetch; only isLoading flips true.
        set({ isLoading: true, error: null });
        
        try {
          // Load session
          const session = await apiService.getSession(sessionId);
          
          // Always load the session players list (needed for sessionPlayers state)
          // Use provided player data if available, otherwise look it up from the players list
          let player;
          let players: any[] = [];
          
          // Load players list (needed for sessionPlayers even if we have playerData)
          // Retry logic: the player might have just been created and not yet visible
          let retryCount = 0;
          const maxRetries = 5;
          while (retryCount < maxRetries) {
            players = await apiService.getSessionPlayers(sessionId);
            
            // If playerData was provided, use it; otherwise find player in the list
            if (playerData) {
              player = playerData;
              // Verify the player exists in the loaded players list
              const playerInList = players.find(p => p.id === playerId);
              if (playerInList) {
                break;
              } else {
                if (retryCount < maxRetries - 1) {
                  await new Promise(resolve => setTimeout(resolve, 300));
                  retryCount++;
                  continue;
                } else {
                  // Player not in list but we have playerData - use it anyway
                  console.warn('[GameStore] loadSessionState: Player not in list after retries, using provided data', { playerId });
                  break;
                }
              }
            } else {
              // Find player in the loaded list
              player = players.find(p => p.id === playerId);
              
              if (player) {
                break;
              }
              
              if (retryCount < maxRetries - 1) {
                await new Promise(resolve => setTimeout(resolve, 300));
                retryCount++;
              } else {
                console.error('[GameStore] loadSessionState: Player not found after retries', { playerId, sessionId });
                throw new Error(`Player ${playerId} not found in session ${sessionId} after ${maxRetries} attempts`);
              }
            }
          }
          
          if (!player) {
            console.error('[GameStore] loadSessionState: Player not found or could not be loaded', { playerId, sessionId });
            throw new Error(`Player ${playerId} not found or could not be loaded`);
          }
          
          // Load adventure details if session has adventure information
          let adventure = null;
          if (session.adventureId) {
            try {
              adventure = await apiService.getAdventureHeader(session.adventureId);
            } catch (adventureError) {
              console.warn('[GameStore] loadSessionState: Failed to load adventure details:', adventureError);
            }
          }

          // Prefetch scene (+ activities) for the *new* session before swapping UI state,
          // so adventure switches do not flash "Scene Not Found" / empty loading frames.
          const adventureId = session.adventureId;
          const scenePromise = apiService.getCurrentScene(sessionId, playerId);
          const activitiesPromise = adventureId
            ? apiService.getAdventureActivities(adventureId).catch((err) => {
                console.warn('[GameStore] loadSessionState: adventure activities fetch failed', err);
                return null;
              })
            : Promise.resolve(null);

          const [sceneResponse, activitiesPayload] = await Promise.all([
            scenePromise,
            activitiesPromise,
          ]);

          const playerWithResponseData = sceneResponse.player
            ? { ...player, ...sceneResponse.player }
            : player;
          const updatedPlayer = sceneResponse.character?.style
            ? { ...playerWithResponseData, style: sceneResponse.character.style }
            : playerWithResponseData;

          const variablesForScene = mergeActivityVariablesFromResponse(
            {},
            sceneResponse.activityVariables ?? null
          );
          const sceneState = prepareSceneState(
            {
              id: sceneResponse.scene.id,
              sections: sceneResponse.scene.sections,
              ...(sceneResponse.scene.style !== undefined && { style: sceneResponse.scene.style }),
            },
            sceneResponse.availableOptionIds || [],
            sceneResponse.waitingOptionIds || [],
            variablesForScene,
            { backOptionId: sceneResponse.backOptionId ?? null }
          );

          // Invalidate in-flight background scene refreshes from the previous session
          sceneApplyEpoch += 1;

          if (import.meta.env.DEV) {
            console.log('[GameStore] loadSessionState: setting session/player state', {
              sessionId: session.id,
              playerId: updatedPlayer.id,
            });
          }

          set({
            currentSession: session,
            currentPlayer: updatedPlayer,
            currentAdventure: adventure,
            sessionPlayers: players,
            sessionStatus: session.status as 'waiting' | 'active' | 'completed' | 'paused',
            selectedAdventure: adventure,
            selectedAdventureId: session.adventureId || null,
            currentScene: sceneState.scene,
            availableOptionIds: sceneState.availableOptionIds,
            waitingOptionIds: sceneState.waitingOptionIds,
            backOptionId: sceneState.backOptionId,
            optionsMap: sceneState.optionsMap,
            userVariableValues: sceneState.userVariableValues,
            adventureActivities: activitiesPayload?.activities ?? [],
            adventureActivityTypes: activitiesPayload?.activityTypes ?? [],
            // Reset leftover state from the previous adventure in the same paint as the new scene
            players: [],
            playerStates: {},
            locationInventories: {},
            waitingConditions: [],
            pendingUserInputs: {},
            isLoading: false,
            error: null,
            sessionError: null,
          });

          if (import.meta.env.DEV) {
            console.log('[GameStore] loadSessionState: loadCurrentScene completed');
          }

          if (useUIStore.getState().actionPendingSince === null) {
            await get().checkAndFireAutoTransition();
          }
          
        } catch (error) {
          console.error('[GameStore] loadSessionState: Error:', error);
          console.error('[GameStore] loadSessionState: Error details:', {
            message: error instanceof Error ? error.message : String(error),
            stack: error instanceof Error ? error.stack : undefined,
            sessionId,
            playerId
          });
          set({
            isLoading: false,
            error: error as ApiError,
          });
          throw error;
        }
      },

      leaveSession: () => {
        const { currentSession, currentPlayer } = get();
        
        if (currentSession && currentPlayer) {
          // TODO: Implement WebSocket leave session
          // websocketService.leaveSession(currentSession.id, currentPlayer.id);
        }
        
        set({
          currentSession: null,
          currentPlayer: null,
          currentScene: null,
          availableOptionIds: [],
          waitingOptionIds: [],
          backOptionId: null,
          optionsMap: {},
          adventureActivities: [],
          adventureActivityTypes: [],
          players: [],
          playerStates: {},
          waitingConditions: [],
          pendingUserInputs: {},
          error: null,
        });
      },

      selectOption: async (optionId: string, options?: { deliverEffectToasts?: boolean }) => {
        const { currentSession, currentPlayer } = get();
        
        if (!currentSession || !currentPlayer) {
          throw new Error('No active session or player');
        }

        // Persist User-Input drafts before option effects read variables (e.g. schedule_real_world_location).
        await get().flushPendingUserInputs();
        
        set({ isLoading: true, error: null });
        
        try {
          const actionResponse = await apiService.performAction(
            currentSession.id,
            currentPlayer.id,
            { action: 'select_option', optionId },
            { timeoutMs: 20000 }
          );

          if (options?.deliverEffectToasts) {
            showAdventureToastsForPlayer(
              actionResponse.toasts,
              getPlayerCharacterName(get().currentPlayer),
              useUIStore.getState().showToast
            );
          }
          
          // Handle START_ADVENTURE action - navigate to new adventure
          if (actionResponse.action === 'START_ADVENTURE' && actionResponse.adventureId) {
            if (import.meta.env.DEV) {
              console.log('[GameStore] START_ADVENTURE: received', {
                adventureId: actionResponse.adventureId,
                sessionId: actionResponse.sessionId,
                playerId: actionResponse.playerId,
              });
            }

            // If backend created the session and returned sessionId and playerId, we are the host — load that session state
            // Do not clearSessionState first: loadSessionState keeps the old scene until an atomic swap.
            if (actionResponse.sessionId && actionResponse.playerId) {
              await get().loadSessionState(actionResponse.sessionId, actionResponse.playerId);
              if (import.meta.env.DEV) {
                const s = get();
                console.log('[GameStore] START_ADVENTURE: loadSessionState completed', {
                  currentSessionId: s.currentSession?.id,
                  currentPlayerId: s.currentPlayer?.id,
                  currentSceneId: s.currentScene?.id,
                });
              }
              // Return false since we're transitioning to a new adventure
              return false;
            } else {
              // Fallback: create a new session for the adventure
              get().clearSessionState();
              if (import.meta.env.DEV) {
                console.log('[GameStore] START_ADVENTURE: cleared session state, loading new session');
              }
              const { sessionId } = await get().createSession(actionResponse.adventureId);
              
              // Join the session
              await get().joinSession(sessionId);
              
              return false;
            }
          }

          // Handle START_OR_RESUME_ADVENTURE - same as START_ADVENTURE (resume existing or load new)
          if (actionResponse.action === 'START_OR_RESUME_ADVENTURE' && actionResponse.adventureId) {
            if (import.meta.env.DEV) {
              console.log('[GameStore] START_OR_RESUME_ADVENTURE: received', {
                adventureId: actionResponse.adventureId,
                sessionId: actionResponse.sessionId,
                playerId: actionResponse.playerId,
              });
            }
            if (actionResponse.sessionId && actionResponse.playerId) {
              await get().loadSessionState(actionResponse.sessionId, actionResponse.playerId);
              return false;
            } else {
              get().clearSessionState();
              const { sessionId } = await get().createSession(actionResponse.adventureId);
              await get().joinSession(sessionId);
              return false;
            }
          }
          
          // Handle LOGGED_IN action - navigate to authenticated user's system adventure
          if (actionResponse.action === 'LOGGED_IN' && actionResponse.sessionId && actionResponse.playerId) {
            // Verify tokens are set in authService (highest priority for axios interceptor)
            // The interceptor now checks authService first, so this is critical
            const authService = (await import('../services/auth')).default;
            const token = authService.getCurrentToken();
            
            if (!token) {
              console.error(`[GameStore] LOGGED_IN: AuthService does not have token! This will cause API calls to fail.`);
              // Try to get token from authStore as fallback (should have been set by waiting-update event)
              const { default: useAuthStore } = await import('./authStore');
              const authStoreState = useAuthStore.getState();
              if (authStoreState.token && !authStoreState.isGuest) {
                (authService as any).setTokens(authStoreState.token, authStoreState.refreshToken || '');
              } else {
                throw new Error('Authentication token not available after login');
              }
            }
            
            // No need for long delay - authService token is synchronous and interceptor checks it first
            // Just a small delay to ensure any pending state updates are processed
            await new Promise(resolve => setTimeout(resolve, 100));
            
            // Join the user's system adventure session (atomic swap — no early clear)
            try {
              await get().loadSessionState(actionResponse.sessionId, actionResponse.playerId);
              
            // Verify state was loaded
            const { currentSession, currentPlayer } = get();
            if (currentSession && currentPlayer) {
              // Navigate to the new session/player URL
              // Use window.location.href to ensure the URL is updated, which is critical for page reloads
              const newUrl = `/scene/${currentSession.id}/${currentPlayer.id}`;
              const currentUrl = window.location.pathname;

              if (currentUrl !== newUrl) {
                // Use window.location.href with replace to update the URL without adding to history
                // This ensures that if the page is reloaded, it will reload the new URL, not the old one
                window.history.replaceState({}, '', newUrl);
                // Also update the location to trigger React Router navigation
                window.location.href = newUrl;
              }
            } else {
              console.error(`[GameStore] LOGGED_IN: Session state not loaded - currentSession=${currentSession?.id}, currentPlayer=${currentPlayer?.id}`);
            }
          } catch (error) {
            console.error(`[GameStore] LOGGED_IN: Failed to load session state:`, error);
            // Re-throw to let ScenePage handle it
            throw error;
          }
          
          // Return false since we're transitioning to a new session
          return false;
          }
          
          // Handle JOIN_ADVENTURE action - navigate to game adventure from system adventure
          // Backend already added this user to the session; load that session state (do not call joinSession again)
          if (actionResponse.action === 'JOIN_ADVENTURE' && actionResponse.sessionId && actionResponse.playerId) {
            if (import.meta.env.DEV) {
              console.log('[GameStore] JOIN_ADVENTURE action received', {
                sessionId: actionResponse.sessionId,
                playerId: actionResponse.playerId,
              });
            }
            await get().loadSessionState(actionResponse.sessionId, actionResponse.playerId);
            const { currentSession, currentPlayer } = get();
            if (currentSession && currentPlayer) {
              const newUrl = `/scene/${currentSession.id}/${currentPlayer.id}`;
              // Soft URL sync only — ScenePage navigates; avoid full reload flash
              if (window.location.pathname !== newUrl) {
                window.history.replaceState({}, '', newUrl);
              }
            }
            if (import.meta.env.DEV) {
              const state = get();
              console.log('[GameStore] JOIN_ADVENTURE loadSessionState done', {
                currentSessionId: state.currentSession?.id,
                currentPlayerId: state.currentPlayer?.id,
              });
            }
            return false;
          }

          if (actionResponse.action === 'RETURN_TO_ADVENTURE' && actionResponse.sessionId && actionResponse.playerId) {
            if (import.meta.env.DEV) {
              console.log('[GameStore] RETURN_TO_ADVENTURE action received', {
                sessionId: actionResponse.sessionId,
                playerId: actionResponse.playerId,
              });
            }
            await get().loadSessionState(actionResponse.sessionId, actionResponse.playerId);
            const { currentSession, currentPlayer } = get();
            if (currentSession && currentPlayer) {
              const newUrl = `/scene/${currentSession.id}/${currentPlayer.id}`;
              // Soft URL sync only — ScenePage navigates; avoid full reload flash
              if (window.location.pathname !== newUrl) {
                window.history.replaceState({}, '', newUrl);
              }
            }
            if (import.meta.env.DEV) {
              const state = get();
              console.log('[GameStore] RETURN_TO_ADVENTURE loadSessionState done', {
                currentSessionId: state.currentSession?.id,
                currentPlayerId: state.currentPlayer?.id,
              });
            }
            return false;
          }
          
          // Merge character style into player if available
          const updatedPlayer = actionResponse.player ? (actionResponse.character ? {
            ...actionResponse.player,
            style: actionResponse.character.style
          } : actionResponse.player) : undefined;
          
          // Update player state
          if (updatedPlayer) {
            set({
              currentPlayer: updatedPlayer,
              isLoading: false,
              error: null,
            });
          } else {
            set({
              isLoading: false,
              error: null,
            });
          }

          // Activity state can change without a scene transition (e.g. startActivity on same-scene option).
          // Merge activity variables immediately so Activity-List reflects new in-progress/completed state.
          if (actionResponse.activityVariables) {
            set((state) => ({
              userVariableValues: mergeActivityVariablesFromResponse(
                state.userVariableValues,
                actionResponse.activityVariables
              )
            }));
          }
          
          // Update scene and options
          if (actionResponse.newScene) {
            const previousSceneId = get().currentScene?.id;
            const sameSceneReload =
              previousSceneId != null && previousSceneId === actionResponse.newScene.id;
            const state = get();
            const existingVariables = sameSceneReload
              ? applyPendingInputsToVariableMap(state.userVariableValues, state.pendingUserInputs)
              : state.userVariableValues;
            const mergeOptions = {
              ...(sameSceneReload
                ? { preserveExistingWhenNewEmpty: true, pendingVariableNames: Object.keys(state.pendingUserInputs) }
                : {}),
              backOptionId: actionResponse.backOptionId ?? null,
            };

            const variablesForScene = mergeActivityVariablesFromResponse(
              existingVariables,
              actionResponse.activityVariables ?? null
            );

            const sceneState = prepareSceneState(
              {
                id: actionResponse.newScene.id,
                sections: actionResponse.newScene.sections,
                ...(actionResponse.newScene.style !== undefined && { style: actionResponse.newScene.style })
              },
              actionResponse.availableOptionIds || [],
              actionResponse.waitingOptionIds || [],
              variablesForScene,
              mergeOptions
            );

            set({
              currentScene: sceneState.scene,
              availableOptionIds: sceneState.availableOptionIds,
              waitingOptionIds: sceneState.waitingOptionIds,
              backOptionId: sceneState.backOptionId,
              optionsMap: sceneState.optionsMap,
              userVariableValues: sceneState.userVariableValues
            });

            // Invalidate in-flight background scene GETs so they cannot overwrite this response.
            sceneApplyEpoch += 1;

            if (sameSceneReload) {
              const pendingNames = Object.keys(get().pendingUserInputs);
              if (import.meta.env.DEV && pendingNames.length > 0) {
                console.log('[GameStore] selectOption same-scene: syncing preserved variables', {
                  pendingVariableNames: pendingNames,
                  currentPlayerId: get().currentPlayer?.id,
                  characterName: get().currentPlayer?.characterName,
                });
              }
              for (const variableName of pendingNames) {
                const entry = sceneState.userVariableValues[variableName];
                const value = entry?.value;
                if (value !== undefined && value !== null && String(value).trim() !== '') {
                  try {
                    await get().submitUserInput(variableName, String(value));
                  } catch (e) {
                    console.warn('[GameStore] Failed to sync preserved variable to backend:', variableName, e);
                  }
                }
              }
            }
          }
          
          // Return completion status
          return updatedPlayer?.completed || false;
          
        } catch (error) {
          set({
            isLoading: false,
            error: error as ApiError,
          });
          throw error;
        }
      },

      selectFinishOption: async (_optionId?: string) => {
        const { currentSession, currentPlayer } = get();
        
        if (!currentSession || !currentPlayer) {
          throw new Error('No active session or player');
        }
        
        set({ isLoading: true, error: null });
        
        try {
          await apiService.selectFinishOption(currentSession.id, currentPlayer.id);
          
          // Clear session state and navigate away (player completed; backend keeps player in session)
          set({
            currentSession: null,
            currentPlayer: null,
            currentScene: null,
            availableOptionIds: [],
            waitingOptionIds: [],
            backOptionId: null,
            optionsMap: {},
            sessionPlayers: [],
            sessionStatus: 'completed',
            selectedAdventure: null,
            selectedAdventureId: null,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          set({
            isLoading: false,
            error: error as ApiError,
          });
          throw error;
        }
      },

      selectLeaveOption: async (_optionId?: string) => {
        const { currentSession, currentPlayer } = get();
        
        if (!currentSession || !currentPlayer) {
          throw new Error('No active session or player');
        }
        
        set({ isLoading: true, error: null });
        
        try {
          await apiService.selectLeaveOption(currentSession.id, currentPlayer.id);
          
          // Clear session state since the player left and slot is freed
          set({
            currentSession: null,
            currentPlayer: null,
            currentScene: null,
            availableOptionIds: [],
            waitingOptionIds: [],
            backOptionId: null,
            optionsMap: {},
            sessionPlayers: [],
            sessionStatus: 'completed',
            selectedAdventure: null,
            selectedAdventureId: null,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          set({
            isLoading: false,
            error: error as ApiError,
          });
          throw error;
        }
      },


      updatePlayerState: (updates: Partial<Player>) => {
        const { currentPlayer } = get();
        
        if (currentPlayer) {
          const updatedPlayer = { ...currentPlayer, ...updates };
          set({ currentPlayer: updatedPlayer });
        }
      },

      setCurrentScene: (scene: { 
        id: string; 
        sections: SceneSection[];
        style?: CustomStyle;
      }) => {
        const { availableOptionIds, waitingOptionIds, userVariableValues, backOptionId } = get();
        const sceneState = prepareSceneState(
          scene,
          availableOptionIds,
          waitingOptionIds,
          userVariableValues,
          { backOptionId }
        );

        set({
          currentScene: sceneState.scene,
          optionsMap: sceneState.optionsMap,
          userVariableValues: sceneState.userVariableValues
        });
      },


      updateGameState: (state: any) => {
        set({
          players: state.players || [],
          playerStates: state.players?.reduce((acc: Record<string, any>, player: any) => {
            acc[player.id] = player;
            return acc;
          }, {}) || {},
        });
      },

      setConnectionStatus: (isConnected: boolean) => {
        if (isConnected) {
          set({ isConnected: true });
        } else {
          set({ isConnected: false, isSocketSessionJoined: false, socketTransport: null });
        }
      },

      setSocketSessionJoined: (joined: boolean) => {
        set({ isSocketSessionJoined: joined });
      },

      setSocketTransport: (transport: string | null) => {
        set({ socketTransport: transport });
      },

      addWaitingCondition: (condition: WaitingCondition) => {
        const { waitingConditions } = get();
        set({
          waitingConditions: [...waitingConditions, condition],
        });
      },

      removeWaitingCondition: (playerId: string, optionId: string) => {
        const { waitingConditions } = get();
        set({
          waitingConditions: waitingConditions.filter(
            condition => !(condition.playerId === playerId && condition.optionId === optionId)
          ),
        });
      },

      setReconnectAttempts: (attempts: number) => {
        set({ reconnectAttempts: attempts });
      },

      clearError: () => {
        set({ error: null });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      setUserVariableValue: (variableName: string, value: string, scope?: UserVariableScope) => {
        set((state) => {
          const previousScope = state.userVariableValues[variableName]?.scope;
          const entry = createVariableValueState(value, scope, previousScope);
          const nextMap: UserVariableMap = {
            ...state.userVariableValues,
            [variableName]: entry
          };

          return {
            userVariableValues: nextMap,
            currentScene: updateSceneWithVariables(state.currentScene, nextMap)
          };
        });
      },

      setUserVariableValues: (values: UserVariableMap) => {
        if (!values || Object.keys(values).length === 0) {
          return;
        }

        set((state) => {
          const sanitizedUpdates: UserVariableMap = {};
          Object.entries(values).forEach(([name, valueState]) => {
            const previousScope = state.userVariableValues[name]?.scope;
            sanitizedUpdates[name] = createVariableValueState(valueState.value, valueState.scope, previousScope);
          });

          const merged = mergeUserVariableMaps(state.userVariableValues, sanitizedUpdates);
          return {
            userVariableValues: merged,
            currentScene: updateSceneWithVariables(state.currentScene, merged)
          };
        });
      },

      setPendingUserInput: (
        variableName: string,
        value: string,
        options?: { maxLength?: number }
      ) => {
        set((state) => {
          const previousScope = state.userVariableValues[variableName]?.scope;
          const entry = createVariableValueState(
            value,
            state.userVariableValues[variableName]?.scope,
            previousScope
          );
          const nextUserVariableValues: UserVariableMap = {
            ...state.userVariableValues,
            [variableName]: entry
          };

          const nextPending = {
            ...state.pendingUserInputs,
            [variableName]: {
              value,
              ...(options?.maxLength !== undefined ? { maxLength: options.maxLength } : {})
            }
          };

          return {
            userVariableValues: nextUserVariableValues,
            currentScene: updateSceneWithVariables(state.currentScene, nextUserVariableValues),
            pendingUserInputs: nextPending
          };
        });
      },

      clearPendingUserInput: (variableName: string) => {
        set((state) => {
          if (!state.pendingUserInputs[variableName]) {
            return state;
          }

          const nextPending = { ...state.pendingUserInputs };
          delete nextPending[variableName];

          return {
            pendingUserInputs: nextPending
          };
        });
      },

      flushPendingUserInputs: async () => {
        const { pendingUserInputs } = get();
        const entries = Object.entries(pendingUserInputs);

        if (entries.length === 0) {
          return;
        }

        for (const [variableName, payload] of entries) {
          const lengthOptions =
            payload.maxLength !== undefined ? { maxLength: payload.maxLength } : undefined;
          await get().submitUserInput(variableName, payload.value, lengthOptions);
        }
      },

      // Adventure selection actions
      loadAdventures: async () => {
        set({ isLoadingAdventures: true, adventureError: null });
        
        try {
          const adventureIds = await apiService.listAdventures();
          const adventures = await Promise.all(
            adventureIds.map(id => apiService.getAdventureHeader(id))
          );
          
          set({
            adventureIds,
            availableAdventures: adventures,
            isLoadingAdventures: false,
            adventureError: null,
          });
        } catch (error) {
          set({
            isLoadingAdventures: false,
            adventureError: (error as ApiError).message || 'Failed to load adventures',
          });
          throw error;
        }
      },

      selectAdventure: (adventureId: string) => {
        const adventureIndex = get().adventureIds.indexOf(adventureId);
        const adventure = adventureIndex >= 0 ? get().availableAdventures[adventureIndex] : null;
        set({ 
          selectedAdventureId: adventureId,
          selectedAdventure: adventure || null
        });
      },

      clearAdventureError: () => {
        set({ adventureError: null });
      },

      // Adventure management
      loadAdventure: async (adventureId: string) => {
        set({ isLoading: true, error: null });
        
        try {
          // Load adventure header (includes style info) instead of full adventure
          const adventure = await apiService.getAdventureHeader(adventureId);
          set({
            currentAdventure: adventure,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          set({
            isLoading: false,
            error: error as ApiError,
          });
          throw error;
        }
      },

      loadAdventureHeader: async (adventureId: string) => {
        set({ isLoading: true, error: null });
        
        try {
          const header = await apiService.getAdventureHeader(adventureId);
          set({
            selectedAdventure: header,
            selectedAdventureId: adventureId,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          set({
            isLoading: false,
            error: error as ApiError,
          });
          throw error;
        }
      },

      loadAdventureScene: async (adventureId: string, sceneId: string) => {
        try {
          return await apiService.getAdventureScene(adventureId, sceneId);
        } catch (error) {
          set({ error: error as ApiError });
          throw error;
        }
      },

      loadAdventureItem: async (adventureId: string, itemName: string) => {
        try {
          return await apiService.getAdventureItem(adventureId, itemName);
        } catch (error) {
          set({ error: error as ApiError });
          throw error;
        }
      },

      loadAdventureStat: async (adventureId: string, statName: string) => {
        try {
          return await apiService.getAdventureStat(adventureId, statName);
        } catch (error) {
          set({ error: error as ApiError });
          throw error;
        }
      },

      createSession: async (adventureId: string) => {
        set({ isLoading: true, isCreatingSession: true, error: null, sessionError: null });
        
        try {
          // Get current user ID for createdBy field
          let authState = useAuthStore.getState();
          
          // Check if this is a system adventure and user is not authenticated
          // If so, use guest session instead
          if (adventureId === get().systemAdventureId && !authState.isAuthenticated) {
            return await get().createGuestSession(adventureId);
          }
          
          // If authenticated but user object is not set, try to fetch it
          if (authState.isAuthenticated && !authState.user?.id && !authState.isGuest) {
            try {
              // Try to get the user from the API
              const user = await authService.getCurrentUser();
              if (user?.id) {
                // Update auth store with user
                useAuthStore.getState().setUser(user);
                // Refresh auth state
                authState = useAuthStore.getState();
              }
            } catch (error) {
              console.warn('Failed to fetch user, continuing with existing auth state', error);
            }
          }
          
          if (!authState.isAuthenticated) {
            throw new Error('User must be logged in to create a session');
          }
          
          // For guest users, use guestUserId; for regular users, use user.id
          const userId = authState.isGuest ? authState.guestUserId : authState.user?.id;
          
          if (!userId) {
            throw new Error('User ID is required to create a session. Please ensure you are logged in.');
          }
          
          const { session, players } = await apiService.createSession({
            adventureId,
            playerIds: [], // Will be populated when players join
            createdBy: userId,
          });
          
          // Load adventure details
          let adventure = null;
          if (session.adventureId) {
            try {
              adventure = await apiService.getAdventureHeader(session.adventureId);
            } catch (adventureError) {
              console.warn('Failed to load adventure details:', adventureError);
            }
          }
          
          // Set current player (should be the first player created - host)
          const currentPlayer = players.length > 0 ? players[0] : null;
          
          // Clear session state and set new session
          get().clearSessionState();
          set({
            currentSession: session,
            currentPlayer: currentPlayer,
            currentAdventure: adventure,
            sessionPlayers: players,
            sessionStatus: session.status as 'waiting' | 'active' | 'completed' | 'paused',
            selectedAdventure: adventure,
            selectedAdventureId: adventureId,
            isCreatingSession: false,
            isLoading: false,
            error: null,
          });
          
          // Automatically start the session to initialize location inventories
          try {
            await apiService.startSession(session.id);
            // Update session status to active
            set({
              sessionStatus: 'active',
            });
          } catch (startError) {
            console.warn('Failed to start session automatically:', startError);
            // Don't throw error - session was created successfully, just not started
          }
          
          // Load current scene and options if player was created
          if (currentPlayer) {
            await get().loadCurrentScene();
          }
          
          if (currentPlayer?.id) {
            return { sessionId: session.id, playerId: currentPlayer.id };
          } else {
            return { sessionId: session.id };
          }
        } catch (error) {
          console.error('[GameStore] createGuestSession failed:', error);
          if (error instanceof Error) {
            console.error('[GameStore] Error message:', error.message);
            console.error('[GameStore] Error stack:', error.stack);
          }
          set({
            isLoading: false,
            isCreatingSession: false,
            error: error as ApiError,
            sessionError: (error as ApiError).message || 'Failed to create session',
          });
          throw error;
        }
      },

      createGuestSession: async (
        adventureId: string,
        invitePayload?: { variables?: Record<string, string> }
      ) => {
        set({ isLoading: true, isCreatingSession: true, error: null, sessionError: null });
        
        let response: Awaited<ReturnType<typeof apiService.createGuestSession>>;
        try {
          // Get existing guest user ID if available (may be stale if backend restarted or guest was purged)
          const existingGuestData = guestService.getGuestData();
          const guestUserId = existingGuestData?.userId;

          // Create guest session (or restore existing guest)
          response = await apiService.createGuestSession(adventureId, guestUserId, invitePayload);
        } catch (firstError) {
          // If backend no longer has this guest (e.g. server restarted, guest purged, or guest was converted to registered user), clear and retry as new guest
          const apiErr = firstError as ApiError;
          const isStaleGuest =
            apiErr?.code === 'USER_NOT_FOUND' ||
            (apiErr?.code === 'VALIDATION_ERROR' &&
              typeof apiErr?.message === 'string' &&
              apiErr.message.includes('not a guest user'));
          if (isStaleGuest) {
            useAuthStore.getState().clearGuestData();
            guestService.clearGuestData();
            response = await apiService.createGuestSession(adventureId, undefined, invitePayload);
          } else {
            set({
              isLoading: false,
              isCreatingSession: false,
              error: firstError as ApiError,
              sessionError: (firstError as ApiError).message || 'Failed to create guest session',
            });
            throw firstError;
          }
        }

        try {
          const { session, player, token, refreshToken, guestUserId: returnedGuestUserId } = response;

          // Store guest tokens in guestService
          guestService.clearGuestData();
          localStorage.setItem('guestUserId', returnedGuestUserId);
          localStorage.setItem('guestToken', token);
          localStorage.setItem('guestRefreshToken', refreshToken);
          
          // Set guest tokens in auth store for API calls
          // IMPORTANT: This must be called BEFORE any navigation to ensure ProtectedRoute allows access
          useAuthStore.getState().setGuestTokens(token, refreshToken, returnedGuestUserId);
          
          // Wait a moment for state to propagate to ensure ProtectedRoute sees isAuthenticated: true
          await new Promise(resolve => setTimeout(resolve, 50));

          // Load adventure details
          let adventure = null;
          if (session.adventureId) {
            try {
              adventure = await apiService.getAdventureHeader(session.adventureId);
            } catch (adventureError) {
              // Adventure loading failed, continue without it
            }
          }

          // Clear session state and set new session
          get().clearSessionState();
          set({
            currentSession: session,
            currentPlayer: player,
            currentAdventure: adventure,
            sessionPlayers: player ? [player] : [],
            sessionStatus: session.status as 'waiting' | 'active' | 'completed' | 'paused',
            selectedAdventure: adventure,
            selectedAdventureId: adventureId,
            isCreatingSession: false,
            isLoading: false,
            error: null,
          });

          // Sessions are active from creation, no need to start
          // Load current scene and options if player was created
          // NOTE: We load the scene here so it's ready when we navigate to ScenePage
          if (player) {
            try {
              await get().loadCurrentScene();
              // Ensure isLoading is false after scene is loaded
              if (get().isLoading) {
                set({ isLoading: false });
              }
            } catch (sceneError) {
              // Ensure isLoading is false even on error
              set({ isLoading: false });
              // Don't throw - we can still return the session/player IDs
              // The ScenePage will try to load the scene again if needed
            }
          }

          const result = player?.id 
            ? { sessionId: session.id, playerId: player.id }
            : { sessionId: session.id };
          
          return result;
        } catch (error) {
          set({
            isLoading: false,
            isCreatingSession: false,
            error: error as ApiError,
            sessionError: (error as ApiError).message || 'Failed to create guest session',
          });
          throw error;
        }
      },

      redeemGuestAdventureInvite: async (token: string) => {
        set({ isLoading: true, isCreatingSession: true, error: null, sessionError: null });

        try {
          const response = await apiService.redeemGuestAdventure(token);
          const { session, player, token: accessToken, refreshToken, guestUserId } = response;

          guestService.saveGuestData({
            userId: guestUserId,
            token: accessToken,
            refreshToken,
          });
          useAuthStore.getState().setGuestTokens(accessToken, refreshToken, guestUserId);
          await new Promise((resolve) => setTimeout(resolve, 50));

          let adventure = null;
          if (session.adventureId) {
            try {
              adventure = await apiService.getAdventureHeader(session.adventureId);
            } catch {
              // Adventure header optional for navigation
            }
          }

          get().clearSessionState();
          set({
            currentSession: session,
            currentPlayer: player,
            currentAdventure: adventure,
            sessionPlayers: player ? [player] : [],
            sessionStatus: session.status as 'waiting' | 'active' | 'completed' | 'paused',
            selectedAdventure: adventure,
            selectedAdventureId: session.adventureId,
            isCreatingSession: false,
            isLoading: false,
            error: null,
          });

          if (player?.id) {
            await get().loadSessionState(session.id, player.id, player);
          }

          if (!player?.id) {
            throw new Error('Guest adventure invite did not include a player');
          }

          return { sessionId: session.id, playerId: player.id };
        } catch (error) {
          set({
            isLoading: false,
            isCreatingSession: false,
            error: error as ApiError,
            sessionError: (error as ApiError).message || 'Failed to redeem guest adventure invite',
          });
          throw error;
        }
      },

      startSession: async () => {
        const { currentSession } = get();
        
        if (!currentSession) {
          throw new Error('No active session');
        }
        
        set({ isLoading: true, error: null });
        
        try {
          await apiService.startSession(currentSession.id);
          
          // Update session status
          set({
            currentSession: {
              ...currentSession,
              status: 'active' as GameStatus,
            } as GameSession,
            isLoading: false,
            error: null,
          });
          
        } catch (error) {
          set({
            isLoading: false,
            error: error as ApiError,
          });
          throw error;
        }
      },

      // Helper method to load current scene
      loadCurrentScene: async (options?: LoadCurrentSceneOptions) => {
        const { currentSession, currentPlayer } = get();

        if (!currentSession || !currentPlayer) {
          console.warn('[GameStore] loadCurrentScene: Missing session or player', {
            hasSession: !!currentSession,
            hasPlayer: !!currentPlayer,
          });
          if (get().isLoading) {
            set({ isLoading: false });
          }
          return;
        }

        const key = `${currentSession.id}:${currentPlayer.id}`;
        if (loadCurrentScenePromise && loadCurrentSceneKey === key) {
          return loadCurrentScenePromise;
        }

        const runLoad = async (): Promise<void> => {
          const state = get();
          const session = state.currentSession;
          const player = state.currentPlayer;
          if (!session || !player) return;
          const fetchKey = `${session.id}:${player.id}`;

          try {
            const epochAtFetchStart = sceneApplyEpoch;
            console.log('[Scene] Loading scene...', {
              sessionId: session.id,
              playerId: player.id,
              backgroundRefresh: Boolean(options?.backgroundRefresh),
            });
            const adventureId = session.adventureId;
            const scenePromise = apiService.getCurrentScene(session.id, player.id);
            const activitiesPromise = adventureId
              ? apiService.getAdventureActivities(adventureId).catch((err) => {
                  console.warn('[GameStore] loadCurrentScene: adventure activities fetch failed', err);
                  return null;
                })
              : Promise.resolve(null);

            const [sceneResponse, activitiesPayload] = await Promise.all([
              scenePromise,
              activitiesPromise
            ]);

            if (options?.backgroundRefresh && epochAtFetchStart !== sceneApplyEpoch) {
              if (import.meta.env.DEV) {
                console.log(
                  '[GameStore] loadCurrentScene: discarded stale background fetch (newer scene/session applied)',
                  { epochAtFetchStart, sceneApplyEpoch }
                );
              }
              return;
            }

            // Session/player may have changed while this GET was in flight (e.g. guest login
            // swapped to the authenticated system session). Never apply with a mismatched
            // player id — that produces PLAYER_NOT_FOUND (guest player + user session).
            const latest = get();
            if (
              latest.currentSession?.id !== session.id ||
              latest.currentPlayer?.id !== player.id
            ) {
              if (import.meta.env.DEV) {
                console.log(
                  '[GameStore] loadCurrentScene: discarded stale fetch (session/player changed)',
                  {
                    fetchedSessionId: session.id,
                    fetchedPlayerId: player.id,
                    currentSessionId: latest.currentSession?.id,
                    currentPlayerId: latest.currentPlayer?.id,
                  }
                );
              }
              return;
            }

            // Merge player data from response to capture dynamic fields like portraitId
            const playerWithResponseData = sceneResponse.player
              ? { ...player, ...sceneResponse.player }
              : player;

          // Merge character style into current player if available
          const updatedPlayer = sceneResponse.character?.style
            ? {
                ...playerWithResponseData,
                style: sceneResponse.character.style,
              }
            : playerWithResponseData;

          const previousSceneId = get().currentScene?.id;
          const sameSceneReload =
            previousSceneId != null && previousSceneId === sceneResponse.scene.id;
          const state = get();
          const existingVariables = sameSceneReload
            ? applyPendingInputsToVariableMap(state.userVariableValues, state.pendingUserInputs)
            : state.userVariableValues;
          const mergeOptions = {
            ...(sameSceneReload
              ? { preserveExistingWhenNewEmpty: true, pendingVariableNames: Object.keys(state.pendingUserInputs) }
              : {}),
            backOptionId: sceneResponse.backOptionId ?? null,
          };

          const variablesForScene = mergeActivityVariablesFromResponse(
            existingVariables,
            sceneResponse.activityVariables ?? null
          );

          const sceneState = prepareSceneState(
            {
              id: sceneResponse.scene.id,
              sections: sceneResponse.scene.sections,
              ...(sceneResponse.scene.style !== undefined && { style: sceneResponse.scene.style })
            },
            sceneResponse.availableOptionIds || [],
            sceneResponse.waitingOptionIds || [],
            variablesForScene,
            mergeOptions
          );

          set({
            currentPlayer: updatedPlayer,
            currentScene: sceneState.scene,
            availableOptionIds: sceneState.availableOptionIds,
            waitingOptionIds: sceneState.waitingOptionIds,
            backOptionId: sceneState.backOptionId,
            optionsMap: sceneState.optionsMap,
            userVariableValues: sceneState.userVariableValues,
            adventureActivities: activitiesPayload?.activities ?? [],
            adventureActivityTypes: activitiesPayload?.activityTypes ?? [],
            // Background (WS) refreshes must not clear isLoading while select_option is in flight
            ...(options?.backgroundRefresh ? {} : { isLoading: false }),
            error: null,
          });

          // After same-scene reload, sync preserved (pending) variable values to the backend
          // so character variables etc. are persisted even before the user clicks Done
          if (sameSceneReload) {
            const pendingNames = Object.keys(get().pendingUserInputs);
            if (import.meta.env.DEV && pendingNames.length > 0) {
              console.log('[GameStore] same-scene reload: syncing preserved variables to backend', {
                pendingVariableNames: pendingNames,
                currentPlayerId: get().currentPlayer?.id,
                characterName: get().currentPlayer?.characterName,
              });
            }
            for (const variableName of pendingNames) {
              const entry = sceneState.userVariableValues[variableName];
              const value = entry?.value;
              if (value !== undefined && value !== null && String(value).trim() !== '') {
                try {
                  await get().submitUserInput(variableName, String(value));
                } catch (e) {
                  console.warn('[GameStore] Failed to sync preserved variable to backend:', variableName, e);
                }
              }
            }
          }

          const sceneId = sceneState.scene?.id;
          const firstSectionDesc = sceneState.scene?.sections?.[0]?.description;
          const sceneName =
            typeof firstSectionDesc === 'string'
              ? firstSectionDesc.slice(0, 80)
              : undefined;
            console.log('[Scene] Scene loaded', {
            sceneId,
            ...(sceneName && { sceneName }),
          });

          if (import.meta.env.DEV && sceneId === 'login_waiting') {
            const loginStatus = sceneState.userVariableValues['user_login_process_status']?.value;
            const loginError = sceneState.userVariableValues['user_login_error']?.value;
            console.log('[Scene] login_waiting state', {
              loginStatus,
              loginError,
              availableOptionIds: sceneState.availableOptionIds,
              waitingOptionIds: sceneState.waitingOptionIds,
            });
          }

          // Check if guest user has completed login and needs token exchange
          // This happens when a guest user logs in via async operation
          // We attempt token exchange whenever we're a guest - the backend will tell us
          // if migration hasn't completed yet, and we'll retry on the next scene load
          // Only attempt exchange once per scene load to avoid infinite loops
          const authStore = (await import('./authStore')).default.getState();
          if (authStore.isGuest && authStore.token) {
            // Check if we've already attempted exchange for this scene
            // Use a simple flag to prevent repeated attempts
            const exchangeKey = `exchange_attempted_${sceneState.scene?.id}`;
            const lastExchangeAttempt = (globalThis as any)[exchangeKey];
            const now = Date.now();
            // Use 10s backoff for migration-not-complete case; 5s for other retries
            const backoffMs = 10000;
            
            if (!lastExchangeAttempt || (now - lastExchangeAttempt) > backoffMs) {
              (globalThis as any)[exchangeKey] = now;
              
              // Try to exchange guest token for user tokens
              // This is safe to call repeatedly - the backend will only succeed if migration has completed
              try {
                await authStore.exchangeGuestToken();
                // Clear the flag on success
                delete (globalThis as any)[exchangeKey];
                
                // Only reload scene when in system adventure - skip for content adventures (e.g. JOIN_ADVENTURE)
                // to avoid racing with session load and causing "Missing session or player"
                const gameState = get();
                if (gameState.currentAdventure?.id === gameState.systemAdventureId) {
                  // After successful token exchange, reload the current scene to ensure
                  // all data is refreshed with the new user context
                  setTimeout(async () => {
                    try {
                      await get().loadCurrentScene();
                    } catch (error) {
                      console.warn('[GameStore] Failed to reload scene after token exchange:', error);
                    }
                  }, 100);
                }
              } catch (error: any) {
                // If exchange fails, it's because migration hasn't completed yet
                // This is expected and we'll try again on next scene load
                if (error?.code === 'VALIDATION_ERROR' && error?.message?.includes('not been migrated')) {
                  // Migration not complete yet - this is expected, will retry later
                  // Don't log as warning since this is normal during the migration process
                } else {
                  // Other errors (network, etc.) - log but don't fail
                  console.warn('[GameStore] Failed to exchange guest token (will retry):', error?.message || error);
                }
              }
            }
          }

          // Fire immediately-available auto-transition options (e.g. exam step start
          // "Continue" with Conditions: true). These are in availableOptionIds, not
          // waitingOptionIds, so useWaitingSceneRefresh polling never picks them up.
          const { default: useUIStore } = await import('./uiStore');
          if (useUIStore.getState().actionPendingSince === null) {
            await get().checkAndFireAutoTransition();
          }
          
          } catch (error) {
            set({
              ...(options?.backgroundRefresh ? {} : { isLoading: false }),
              error: error as ApiError,
            });
            throw error;
          } finally {
            // Only clear coalescing state if we are still the in-flight load for this key
            // (a newer load for a different session/player may have replaced the tracking vars).
            if (loadCurrentSceneKey === fetchKey) {
              loadCurrentScenePromise = null;
              loadCurrentSceneKey = null;
            }
          }
        };

        loadCurrentSceneKey = key;
        loadCurrentScenePromise = runLoad();
        return loadCurrentScenePromise;
      },

      checkAndFireAutoTransition: async () => {
        if (autoTransitionInProgress) return;
        const { availableOptionIds, optionsMap, selectOption, currentScene } = get();
        const optionId = availableOptionIds.find(
          (id) => optionsMap[id]?.autoTransition === true
        );
        if (!optionId) return;
        if (import.meta.env.DEV && currentScene?.id === 'login_waiting') {
          console.log('[GameStore] checkAndFireAutoTransition: firing login_waiting auto-transition', {
            optionId,
            target: optionsMap[optionId]?.target,
          });
        }
        autoTransitionInProgress = true;
        try {
          await selectOption(optionId);
        } finally {
          autoTransitionInProgress = false;
        }
      },

      // Session lobby actions
      joinSessionByCode: async (sessionCode: string) => {
        set({ isLoading: true, sessionError: null });
        
        try {
          const session = await apiService.getSession(sessionCode);
          const players = await apiService.getSessionPlayers(sessionCode);
          
          // Load adventure details if session has adventure information
          let adventure = null;
          if (session.adventureId) {
            try {
              adventure = await apiService.getAdventureHeader(session.adventureId);
            } catch (adventureError) {
              console.warn('Failed to load adventure details:', adventureError);
              // Continue without adventure details - session will still work
            }
          }
          
          

          // Clear session state and set new session data
          get().clearSessionState();
          set({
            currentSession: session,
            sessionPlayers: players,
            sessionStatus: session.status as 'waiting' | 'active' | 'completed' | 'paused',
            selectedAdventure: adventure,
            selectedAdventureId: session.adventureId || null,
          });
        } catch (error) {
          set({
            isLoading: false,
            sessionError: (error as ApiError).message || 'Failed to join session',
          });
          throw error;
        }
      },

      clearSessionError: () => {
        set({ sessionError: null });
      },

      loadSessionPlayers: async (sessionId: string) => {
        try {
          const players = await apiService.getSessionPlayers(sessionId);
          set({ sessionPlayers: players });
        } catch (error) {
          set({ sessionError: (error as ApiError).message || 'Failed to load players' });
        }
      },

      listPlaythroughSnapshots: async () => {
        const { currentSession } = get();
        if (!currentSession) {
          throw new Error('No active session');
        }
        return apiService.listPlaythroughSnapshots(currentSession.id);
      },

      loadPlaythroughSnapshot: async (snapshotPath: string) => {
        const { currentSession, currentPlayer } = get();
        if (!currentSession || !currentPlayer) {
          throw new Error('No active session or player');
        }

        set({ isLoading: true, error: null });
        try {
          const result = await apiService.loadPlaythroughSnapshot(currentSession.id, snapshotPath);
          await get().loadSessionPlayers(currentSession.id);
          await get().loadCurrentScene();
          set({ isLoading: false, error: null });
          return result;
        } catch (error) {
          set({ isLoading: false, error: error as ApiError });
          throw error;
        }
      },



      // Helper actions
      loadSystemSession: async (inviteVariables?: Record<string, string>) => {
        set({ isLoading: true, error: null });
        
        try {
          const { session, player } = await apiService.getSystemSession(inviteVariables);
          
          let adventure = null;
          if (session.adventureId) {
            try {
              adventure = await apiService.getAdventureHeader(session.adventureId);
            } catch (adventureError) {
              console.warn('Failed to load adventure details:', adventureError);
            }
          }
          
          let currentScene = null;
          let availableOptionIds: string[] = [];
          let waitingOptionIds: string[] = [];
          let updatedPlayer: any = player;
          
          try {
            const adventureIdForActivities = session.adventureId;
            const scenePromise = apiService.getCurrentScene(session.id, player.id);
            const activitiesPromise = adventureIdForActivities
              ? apiService.getAdventureActivities(adventureIdForActivities).catch((err) => {
                  console.warn('[GameStore] loadSystemSession: adventure activities fetch failed', err);
                  return null;
                })
              : Promise.resolve(null);

            const [sceneResponse, activitiesPayload] = await Promise.all([
              scenePromise,
              activitiesPromise
            ]);
            
            updatedPlayer = sceneResponse.character ? {
              ...player,
              style: sceneResponse.character.style
            } : player;
            
            const previousSceneId = get().currentScene?.id;
            const sameSceneReload =
              previousSceneId != null && previousSceneId === sceneResponse.scene.id;
            const state = get();
            const existingVariables = sameSceneReload
              ? applyPendingInputsToVariableMap(state.userVariableValues, state.pendingUserInputs)
              : state.userVariableValues;
            const mergeOptions = {
              ...(sameSceneReload
                ? { preserveExistingWhenNewEmpty: true, pendingVariableNames: Object.keys(state.pendingUserInputs) }
                : {}),
              backOptionId: sceneResponse.backOptionId ?? null,
            };

            const variablesForScene = mergeActivityVariablesFromResponse(
              existingVariables,
              sceneResponse.activityVariables ?? null
            );

            const sceneState = prepareSceneState(
              {
                id: sceneResponse.scene.id,
                sections: sceneResponse.scene.sections,
                ...(sceneResponse.scene.style !== undefined && { style: sceneResponse.scene.style })
              },
              sceneResponse.availableOptionIds || [],
              sceneResponse.waitingOptionIds || [],
              variablesForScene,
              mergeOptions
            );

            currentScene = sceneState.scene;
            availableOptionIds = sceneState.availableOptionIds;
            waitingOptionIds = sceneState.waitingOptionIds;

            // Invalidate in-flight guest/background scene refreshes so they cannot overwrite
            // the authenticated system session (mismatched guest player id + user session).
            sceneApplyEpoch += 1;

            set({
              currentSession: session,
              currentPlayer: updatedPlayer,
              currentAdventure: adventure,
              currentScene: sceneState.scene,
              sessionPlayers: [updatedPlayer],
              sessionStatus: session.status as 'waiting' | 'active' | 'completed' | 'paused',
              selectedAdventure: adventure,
              selectedAdventureId: session.adventureId || null,
              availableOptionIds: sceneState.availableOptionIds,
              waitingOptionIds: sceneState.waitingOptionIds,
              backOptionId: sceneState.backOptionId,
              optionsMap: sceneState.optionsMap,
              userVariableValues: sceneState.userVariableValues,
              adventureActivities: activitiesPayload?.activities ?? [],
              adventureActivityTypes: activitiesPayload?.activityTypes ?? [],
              isLoading: false,
              error: null,
            });

            if (sameSceneReload) {
              const pendingNames = Object.keys(get().pendingUserInputs);
              if (import.meta.env.DEV && pendingNames.length > 0) {
                console.log('[GameStore] session load same-scene: syncing preserved variables', {
                  pendingVariableNames: pendingNames,
                  currentPlayerId: get().currentPlayer?.id,
                  characterName: get().currentPlayer?.characterName,
                });
              }
              for (const variableName of pendingNames) {
                const entry = sceneState.userVariableValues[variableName];
                const value = entry?.value;
                if (value !== undefined && value !== null && String(value).trim() !== '') {
                  try {
                    await get().submitUserInput(variableName, String(value));
                  } catch (e) {
                    console.warn('[GameStore] Failed to sync preserved variable to backend:', variableName, e);
                  }
                }
              }
            }

            return { sessionId: session.id, playerId: player.id };
          } catch (sceneError) {
            console.warn('Failed to load current scene:', sceneError);
          }

          // Still bump epoch when falling back so in-flight guest refreshes cannot overwrite.
          sceneApplyEpoch += 1;
          
          set({
            currentSession: session,
            currentPlayer: updatedPlayer,
            currentAdventure: adventure,
            currentScene,
            sessionPlayers: [updatedPlayer],
            sessionStatus: session.status as 'waiting' | 'active' | 'completed' | 'paused',
            selectedAdventure: adventure,
            selectedAdventureId: session.adventureId || null,
            availableOptionIds,
            waitingOptionIds,
            isLoading: false,
            error: null,
          });
          
          return { sessionId: session.id, playerId: player.id };
        } catch (error) {
          set({
            isLoading: false,
            error: error as ApiError,
          });
          throw error;
        }
      },

      clearSessionState: () => {
        if (import.meta.env.DEV) {
          const stack = new Error().stack;
          console.log('[GameStore] clearSessionState called', {
            caller: stack?.split('\n').slice(1, 4).join(' <- '),
          });
        }
        set({
          currentSession: null,
          currentPlayer: null,
          currentAdventure: null,
          currentScene: null,
          sessionPlayers: [],
          sessionStatus: 'waiting',
          selectedAdventure: null,
          selectedAdventureId: null,
          adventureActivities: [],
          adventureActivityTypes: [],
          players: [],
          playerStates: {},
          locationInventories: {},
          availableOptionIds: [],
          waitingOptionIds: [],
          backOptionId: null,
          optionsMap: {},
          waitingConditions: [],
          pendingUserInputs: {},
          userVariableValues: {},
          isLoading: false,
          error: null,
          sessionError: null,
        });
      },

      // Session management actions
      deleteSession: async (sessionId: string) => {
        set({ isLoading: true, error: null });
        
        try {
          await apiService.deleteSession(sessionId);
          
          // Clear current session data
          get().clearSessionState();
        } catch (error) {
          set({
            isLoading: false,
            error: error as ApiError,
          });
          throw error;
        }
      },

      leaveSessionById: async (sessionId: string) => {
        set({ isLoading: true, error: null });
        
        try {
          await apiService.leaveSession(sessionId);
          
          // Clear current session data
          get().clearSessionState();
        } catch (error) {
          set({
            isLoading: false,
            error: error as ApiError,
          });
          throw error;
        }
      },

      submitUserInput: async (variableName: string, value: string, options?: { maxLength?: number }) => {
        const { currentSession, currentPlayer, userVariableValues } = get();

        if (!currentSession || !currentPlayer) {
          throw new Error('No active session or player');
        }

        const sceneIdAtSubmit = get().currentScene?.id;
        const epochAtSubmit = sceneApplyEpoch;

        try {
          const payload: { variableName: string; value?: string; maxLength?: number } = {
            variableName,
            value
          };

          if (options?.maxLength !== undefined) {
            payload.maxLength = options.maxLength;
          }

          if (import.meta.env.DEV) {
            console.log('[GameStore] submitUserInput', {
              variableName,
              playerId: currentPlayer.id,
              characterName: currentPlayer.characterName,
              valueLength: value.length,
            });
          }
          const response = await apiService.submitUserInput(currentSession.id, currentPlayer.id, payload);
          if (import.meta.env.DEV && response?.scope === 'character') {
            console.log('[GameStore] submitUserInput response (character)', {
              variableName,
              characterScope: true,
            });
          }

          const updatedPlayer = response.player
            ? { ...currentPlayer, ...response.player }
            : currentPlayer;

          const variableUpdate: UserVariableMap = {
            [variableName]: createVariableValueState(
              response.value ?? value ?? '',
              response.scope,
              userVariableValues[variableName]?.scope
            )
          };

          const mergedVariables = mergeActivityVariablesFromResponse(
            mergeUserVariableMaps(userVariableValues, variableUpdate),
            response.activityVariables ?? null
          );

          const currentSceneId = get().currentScene?.id;
          const responseScene = response.scene;
          const sceneResponseStillCurrent =
            responseScene &&
            responseScene.id === sceneIdAtSubmit &&
            responseScene.id === currentSceneId &&
            epochAtSubmit === sceneApplyEpoch;

          const sceneState = sceneResponseStillCurrent
            ? prepareSceneState(
                {
                  id: responseScene.id,
                  sections: responseScene.sections,
                  ...(responseScene.style !== undefined && { style: responseScene.style })
                },
                response.availableOptionIds || [],
                response.waitingOptionIds || [],
                mergedVariables,
                { backOptionId: response.backOptionId ?? null }
              )
            : {
                scene: updateSceneWithVariables(get().currentScene, mergedVariables),
                availableOptionIds: get().availableOptionIds,
                waitingOptionIds: get().waitingOptionIds,
                backOptionId: get().backOptionId,
                optionsMap: get().optionsMap,
                userVariableValues: mergedVariables
              };

          const pendingInputs = get().pendingUserInputs;
          const nextPendingInputs = { ...pendingInputs };
          delete nextPendingInputs[variableName];

          set({
            currentPlayer: updatedPlayer,
            currentScene: sceneState.scene,
            availableOptionIds: sceneState.availableOptionIds,
            waitingOptionIds: sceneState.waitingOptionIds,
            backOptionId: sceneState.backOptionId,
            optionsMap: sceneState.optionsMap,
            userVariableValues: sceneState.userVariableValues,
            pendingUserInputs: nextPendingInputs,
            isLoading: false,
            error: null
          });

          return response;
        } catch (error) {
          set({
            isLoading: false,
            error: error as ApiError
          });
          throw error;
        }
      },

      // Location inventory actions
      updateLocationInventory: (locationTag: string, items: string[]) => {
        const { locationInventories } = get();
        set({
          locationInventories: {
            ...locationInventories,
            [locationTag]: items
          }
        });
      },

      addItemToLocation: (locationTag: string, itemTag: string) => {
        const { locationInventories } = get();
        const currentItems = locationInventories[locationTag] || [];
        if (!currentItems.includes(itemTag)) {
          set({
            locationInventories: {
              ...locationInventories,
              [locationTag]: [...currentItems, itemTag]
            }
          });
        }
      },

      removeItemFromLocation: (locationTag: string, itemTag: string) => {
        const { locationInventories } = get();
        const currentItems = locationInventories[locationTag] || [];
        set({
          locationInventories: {
            ...locationInventories,
            [locationTag]: currentItems.filter(item => item !== itemTag)
          }
        });
      },

      getLocationInventory: (locationTag: string) => {
        const { locationInventories } = get();
        return locationInventories[locationTag] || [];
      },
    }),
    {
      name: 'game-storage',
      version: 2,
      storage: createJSONStorage(() => localStorage),
      // Persist session identity only — scene and variables must come from the server on load/resume.
      partialize: (state) => ({
        currentSession: state.currentSession,
        currentPlayer: state.currentPlayer,
        currentAdventure: state.currentAdventure,
        players: state.players,
        playerStates: state.playerStates,
      }),
      migrate: (persistedState: any, version: number) => {
        let state = persistedState ?? {};
        if (version === 0) {
          state = {
            ...state,
            availableOptionIds: [],
            waitingOptionIds: [],
          };
        }
        if (version < 2) {
          const {
            userVariableValues: _uv,
            currentScene: _scene,
            availableOptionIds: _opts,
            waitingOptionIds: _wait,
            optionsMap: _map,
            ...rest
          } = state;
          state = rest;
        }
        return state;
      },
    }
  )
);

export default useGameStore;
